import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Segmented } from 'antd';
import TierList from '../../components/TierList/TierList';
import TierDiagram from '../../components/TierDiagram/TierDiagram';
import { usePartnershipContext } from '../../../../core/context/PartnershipContext';
import EntityService from '../../../EntityView/services/EntityService';
import styles from './TieringView.module.css';

const TieringView: React.FC = () => {
  const { entityId } = useParams<{ entityId: string }>();
  const navigate = useNavigate();
  const [view, setView] = useState<string | number>('List');
  const [noPartnershipMsg, setNoPartnershipMsg] = useState(false);
  const id = Number(entityId ?? 0);
  const { selectedClient, setSelectedPartnership } = usePartnershipContext();

  // Sync selectedPartnership context when navigated directly to /tiering/:entityId
  useEffect(() => {
    if (!id) return;
    EntityService.getEntityById(id)
      .then((entity) => {
        setSelectedPartnership({
          id: String(entity.id),
          name: entity.name,
          entityType: entity.entityType || 'Partnership',
        });
      })
      .catch(() => {});
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  // Bug 2: When no entityId in URL but a client is selected, auto-navigate to
  // the first partnership entity for that client so the diagram/list can load.
  useEffect(() => {
    if (id || !selectedClient) return;
    setNoPartnershipMsg(false);
    EntityService.getEntitiesByClient(selectedClient.id)
      .then((entities) => {
        const firstPartnership = entities.find(
          (e) =>
            e.entityType === 'Partnership' ||
            e.entityType === 'LLC' ||
            e.category?.includes('Partnership') ||
            e.category?.includes('LP'),
        );
        if (firstPartnership) {
          navigate(`/tiering/${firstPartnership.id}`, { replace: true });
        } else {
          setNoPartnershipMsg(true);
        }
      })
      .catch(() => {});
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedClient, id]);

  if (!id && noPartnershipMsg) {
    return (
      <div className={styles.page}>
        <div className={styles.header}>
          <h1 className={styles.title}>Tiering Relationships</h1>
        </div>
        <div className={styles.content} style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '60vh' }}>
          <p style={{ color: 'rgba(255,255,255,0.45)', textAlign: 'center', maxWidth: 480 }}>
            No partnership entities found for this client. Create an entity first, then return here to set up tiering.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.page}>
      <div className={styles.header}>
        <h1 className={styles.title}>Tiering Relationships</h1>
        <Segmented
          options={['List', 'Diagram']}
          value={view}
          onChange={setView}
          className={styles.segmented}
        />
      </div>
      <div className={styles.content}>
        {view === 'List' ? (
          <TierList entityId={id} />
        ) : (
          <TierDiagram rootEntityId={id} />
        )}
      </div>
    </div>
  );
};

export default TieringView;
